import { ChangeEvent, FormEvent, forwardRef, MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEnterSubmit } from "@libs/zapi-ui/custom";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea
} from "@libs/zapi-ui/ds/components/input-group";
import { ArrowUpIcon, CheckIcon } from "@phosphor-icons/react";
import { useCommentStore } from "../model/store/commentsStore";
import { generateStubComment } from "../lib/generateStubComment";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useEditorStore } from "@/zapi-frontend/stores/editor";

type TReplyEditInputFieldProps = {
  firstName?: string;
  lastName?: string;
  userId: number;
  commentId: string;
  commentContent?: string;
  isEditField?: boolean;
  parentId?: string;
  onSubmit?: () => void;
  onCancel: () => void;
};

export const ReplyEditInputField = forwardRef<HTMLTextAreaElement, TReplyEditInputFieldProps>((props, ref) => {
  const {
    userId,
    firstName = "",
    lastName = "",
    commentId,
    commentContent = "",
    isEditField = false,
    parentId,
    onSubmit,
    onCancel
  } = props;

  const { t } = useTranslation("projectHome");
  const { createComment, updateComment } = useCommentStore();
  const { activeChapter } = useChapterStore();
  const { selectedProject } = useProjectStore();
  const { commentsUpdated } = useEditorStore();

  const projectId = selectedProject?.id;
  const chapterId = activeChapter?.id.toString() || "";

  const [message, setMessage] = useState(commentContent);

  function handleInputChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setMessage(e.target.value);
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage || !projectId || !chapterId) return;

    setMessage("");
    onSubmit?.();

    if (isEditField) {
      await updateComment({ projectId, chapterId, commentId, content: trimmedMessage, parentId });
      commentsUpdated?.();
      return;
    }

    await createComment({
      projectId,
      chapterId,
      content: trimmedMessage,
      stubComment: generateStubComment({ userId, firstName, lastName, content: trimmedMessage }),
      parentId: commentId
    });

    commentsUpdated?.();
  };

  function handleCancel(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setMessage("");
    onCancel?.();
  }

  const fieldLabel = isEditField ? t("comments.editComment") : t("comments.reply");

  return (
    <form onClick={(event) => event.stopPropagation()} onSubmit={handleSubmit}>
      <InputGroup className="rounded-xl bg-background">
        <InputGroupTextarea
          ref={ref}
          value={message}
          onChange={handleInputChange}
          onKeyDown={(event) => {
            if (isEnterSubmit(event)) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder={fieldLabel}
          aria-label={fieldLabel}
          rows={1}
          data-testid="comment-input"
          className="typo-body-sm max-h-28 min-h-9 px-3 pt-2.5"
        />
        <InputGroupAddon align="block-end" className="justify-end gap-1 pe-1.5 pb-1.5">
          {(!!message || isEditField) && (
            <InputGroupButton
              variant="ghost"
              size="xs"
              className="rounded-full text-muted-foreground"
              data-testid="reply-input-cancel-button"
              onClick={handleCancel}
            >
              {t("comments.cancel")}
            </InputGroupButton>
          )}
          <InputGroupButton
            type="submit"
            variant="default"
            size="icon-xs"
            className="rounded-full"
            aria-label={isEditField ? t("comments.save") : t("comments.sendReply")}
            disabled={!message.trim()}
            data-testid="post-comment-button"
          >
            {isEditField ? <CheckIcon weight="bold" /> : <ArrowUpIcon weight="bold" />}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </form>
  );
});
